import { useState, useCallback, useEffect } from "react";
import de from "@/i18n/admin/de";
import tr from "@/i18n/admin/tr";

export type AdminLang = "de" | "tr";

const STORAGE_KEY = "admin_lang";

const translations: Record<AdminLang, Record<string, string>> = { de, tr };

function getStoredLang(): AdminLang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "de" || stored === "tr") return stored;
  } catch {
    // ignore
  }
  return "de";
}

// Singleton state so all components share the same language
let currentLang: AdminLang = getStoredLang();
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

export function useAdminI18n() {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const update = () => forceUpdate((n) => n + 1);
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, []);

  const setLang = useCallback((lang: AdminLang) => {
    currentLang = lang;
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
    notifyListeners();
  }, []);

  const t = useCallback(
    (key: string, fallback?: string): string => {
      return translations[currentLang][key] ?? translations["de"][key] ?? fallback ?? key;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLang]
  );

  return {
    lang: currentLang,
    setLang,
    t,
  };
}
